import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeConfigurationDialog, {
  NodeConfiguration,
} from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
// @ts-ignore
import { useReactFlow, NodeProps } from '@xyflow/react'
import { Steps } from 'intro.js-react'
import { UserPlus, Settings, Filter, HelpCircle } from 'lucide-react'
import React, { memo, useRef, useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
// @ts-ignore
import '~/styles/introjs.css'


import useDataSettings from '@/hooks/useDataSettings'
import useIntroTour from '@/hooks/useIntroTour'
import useQuickStartState from '@/hooks/useQuickStartState'
import { useGetDocuments } from '@/stores/documentLibraryStore'
import styles from '~/styles/NodeQuickStartDynamic.module.css'
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  createNodeCommandPayload,
} from '@/utils/aiCommandUtils'
import NodeDataSettingsDialog, {
  NodeDataSettings,
} from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

interface NodeDataQuickStartDynamic {
  challenge?: string
  desiredOutcome?: string
  statusMessage?: string
  configuration?: NodeConfiguration
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  assignedEmails?: string
  dataSettings?: NodeDataSettings
  linkedKnowledge?: string
  lastModified?: number
  selectedTemplateKey?: string
  documentRequest?: {
    document: string
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
}

const NodeQuickStartDynamic: React.FC<
  NodeProps & { data: NodeDataQuickStartDynamic }
> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  const titleInputRef = useRef<HTMLInputElement>(null)
  const { introEnabled, setIntroEnabled, introSteps, introOptions } =
    useIntroTour(id, null)

  // ✅ ADD: Initialize minimizeLevel if not set
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    }
  }, [id, data, updateNodeData])

  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] =
    useState(false)

  // ✅ ADD: Local state for textareas like QuickStartNode
  const [localChallenge, setLocalChallenge] = useState('')
  const [localDesiredOutcome, setLocalDesiredOutcome] = useState('')

  const { documents, fetchDocuments, documentsLoading, documentsFetchSuccess } =
    useGetDocuments()

  useEffect(() => {
    fetchDocuments().catch(console.error)
  }, [fetchDocuments])

  const templateList = documents
    ? documents.map((doc) => ({ key: doc.documentKey, title: doc.title }))
    : []

  const {
    challenge,
    setChallenge,
    desiredOutcome,
    setDesiredOutcome,
    statusMessage,
    setStatusMessage,
    assignedEmails,
    setAssignedEmails,
    isLocked,
    setIsLocked,
    isMinimized,
    setIsMinimized,
    nodeConfig,
    setNodeConfig,
    isConfigDialogVisible,
    setIsConfigDialogVisible,
    showDebugInfo,
    setShowDebugInfo,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleProcessClick,
    handleTitleChange,
  } = useQuickStartState(id, data, updateNodeData)

  // ✅ ADD: Sync local state with hook state
  useEffect(() => {
    setLocalChallenge(challenge || '')
  }, [challenge])

  useEffect(() => {
    setLocalDesiredOutcome(desiredOutcome || '')
  }, [desiredOutcome])

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    showDebugInfo,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  )

  // Helper to prepare exposedData fields for nodequickstartdynamic
  const prepareExposedDataFields = () => ({
    challenge: {
      value: challenge,
      enabled: true,
      label: 'Challenge',
      description: 'The challenge that needs to be addressed.',
      permissions: { readable: true, writable: false },
    },
    desiredOutcome: {
      value: desiredOutcome,
      enabled: true,
      label: 'Desired Outcome',
      description: 'The outcome as envisioned to resolve the challenge.',
      permissions: { readable: true, writable: false },
    },
    linkedKnowledge: {
      value: data.linkedKnowledge || '',
      enabled: true,
      label: 'Linked Knowledge',
      description: 'Knowledge imported from linked nodes.',
      permissions: { readable: true, writable: false },
    },
  })

  // Keep exposedData in sync with challenge/desiredOutcome/linkedKnowledge
  useEffect(() => {
    const newExposed = prepareExposedDataFields()
    setDataSettings((prev) => ({
      ...prev,
      exposedData: newExposed,
    }))
    // Sync exposed data to node data so other nodes can see it
    syncNodeData({
      dataSettings: {
        ...(data.dataSettings || {}),
        exposedData: newExposed,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge, desiredOutcome, data.linkedKnowledge])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (challenge && challenge.trim()) {
      minifiedInfo.challenge = challenge
    }

    if (desiredOutcome && desiredOutcome.trim()) {
      minifiedInfo['desired outcome'] = desiredOutcome
    }

    if (data.linkedKnowledge?.trim()) {
      minifiedInfo['linked knowledge'] = data.linkedKnowledge
    }

    if (data.selectedTemplateKey) {
      const selectedTemplate = templateList.find(
        (t) => t.key === data.selectedTemplateKey
      )
      if (selectedTemplate) {
        minifiedInfo.template = selectedTemplate.title
      }
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      syncNodeData({ minifiedInfo })
    }
  }, [
    challenge,
    desiredOutcome,
    data.linkedKnowledge,
    data.selectedTemplateKey,
    templateList,
    data.minifiedInfo,
    syncNodeData,
  ])

  const [isDarkMode, setIsDarkMode] = useState(false)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(
        document.documentElement.getAttribute('data-theme') === 'dark'
      )
    }
    checkDarkMode()
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [])

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKey = e.target.value
    syncNodeData({ selectedTemplateKey: selectedKey })
  }

  const customButtons = [
    {
      id: '1',
      onClick: handleAssignUsersClick,
      icon: <UserPlus size={16} />,
      tooltip: 'Assign user(s)',
    },
    {
      id: '2',
      onClick: handleConfigClick,
      icon: <Settings size={16} />,
      tooltip: 'Settings',
    },
    {
      id: '3',
      onClick: () => setIntroEnabled(true),
      icon: <HelpCircle size={16} />,
      tooltip: 'Help & Guide',
    },
  ]

  const filteredCustomButtons = customButtons.filter(
    (button) => !['1', '2'].includes(button.id)
  )
  filteredCustomButtons.push({
    id: '5',
    onClick: handleOpenDataSettingsDialog,
    icon: <Filter size={16} />,
    tooltip: 'Data connection settings',
  })

  function handleAssignUsersClick() {
    const emailsStr = window.prompt(
      'Enter emails separated by space',
      Array.isArray(assignedEmails)
        ? assignedEmails.join(' ')
        : assignedEmails || ''
    )
    if (emailsStr !== null) {
      const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
      // ✅ CORRECT: Only syncNodeData, no local setter call
      syncNodeData({ assignedEmails: emailsArr })
    }
  }

  function handleConfigClick() {
    setIsConfigDialogVisible(true)
  }

  function containsURL(text: string) {
    return /\bhttps?:\/\/[^\s]+/i.test(text)
  }

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (data.documentRequest?.['sysagent-cmd']?.includes('Started'))
      return 'loading'
    if (statusMessage && statusMessage.toLowerCase().includes('error'))
      return 'error'
    if (statusMessage && statusMessage.toLowerCase().includes('complete'))
      return 'success'
    return 'initial'
  }, [data.documentRequest?.['sysagent-cmd'], statusMessage])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={true}
      nodeTitle={data.label || 'Quick Start Dynamic'}
      isLocked={isLocked}
      isMinimized={data.minimizeLevel !== 'normal'}
      minimizeLevel={data.minimizeLevel || 'normal'}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimize}
      onMinimizeLevelChange={(level) =>
        syncNodeData({ minimizeLevel: level, isMinimized: level !== 'normal' })
      }
      onTitleChange={handleTitleChange}
      customButtons={filteredCustomButtons}
      assignedEmails={
        Array.isArray(assignedEmails)
          ? assignedEmails
          : assignedEmails
            ? assignedEmails.trim().split(/\s+/).filter(Boolean)
            : []
      }
      onAssignUsersClick={handleAssignUsersClick}
      onConfigClick={handleConfigClick}
      status={nodeStatus}
      selected={selected} // ✅ CRITICAL: Add the missing selected prop!
    >
      {data.minimizeLevel === 'normal' && (
        <div
          className={`${styles.nodeContent} quickstart-input flex flex-col h-full`}
        >
          {/* Top section: Template selector and textareas - takes available space */}          <div className="flex-grow flex flex-col gap-2 min-h-0">
            <div className={`${styles.templateSelector} flex-shrink-0`}>
              <label className="block text-sm font-medium mb-1">
                Select Template:
              </label>
              <select
                value={data.selectedTemplateKey || ''}
                onChange={handleTemplateChange}
                disabled={isLocked}
                className="w-full border rounded px-2 py-1"
              >
                <option value="">-- Choose Template --</option>
                {templateList.map((tpl) => (
                  <option key={tpl.key} value={tpl.key}>
                    {tpl.title}
                  </option>
                ))}
              </select>
            </div>
            <div
              className={`${styles.textAreaContainer} flex-grow flex flex-col gap-2 min-h-0`}
            >
              <textarea
                placeholder="Challenge: Describe the challenge, whether simple or complex, that needs to be addressed."
                value={localChallenge}
                onChange={(e) => setLocalChallenge(e.target.value)}
                onBlur={() => {
                  syncNodeData({
                    challenge: localChallenge,
                    lastModified: Date.now(),
                  })
                }}
                disabled={isLocked}
                className={`${styles.textArea} nodrag nowheel flex-grow min-h-0`}
              />
              <textarea
                placeholder="Desired Outcome: Describe the outcome as you envision it—how things should be in the end to resolve or neutralize the challenge."
                value={localDesiredOutcome}
                onChange={(e) => setLocalDesiredOutcome(e.target.value)}
                onBlur={() => {
                  syncNodeData({
                    desiredOutcome: localDesiredOutcome,
                    lastModified: Date.now(),
                  })
                }}
                disabled={isLocked}
                className={`${styles.textArea} nodrag nowheel flex-grow min-h-0`}
              />
              <div className="text-xs text-muted-foreground mb-2">
                Note: URLs entered into challenge or desired outcome are not processed or followed; they are treated as plain text only.
              </div>
            </div>
          </div>

          {/* Bottom section: Proceed button and status message - fixed at bottom */}
          <div className="flex flex-col gap-2 flex-shrink-0 mt-2">
            {!isLocked && (
              <Button
                className={`${styles.processButton} quickstart-proceed-button`}
                onClick={handleProcessClick}
                variant={isDarkMode ? 'outline' : 'default'}
              >
                Proceed
              </Button>
            )}
            {statusMessage && (
              <div className={styles.statusLabel}>{statusMessage}</div>
            )}
          </div>
        </div>
      )}

      {isConfigDialogVisible && !isLocked && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}
          configuration={nodeConfig}
          onSave={(newConfig) => {
            setNodeConfig(newConfig)
            syncNodeData({ configuration: newConfig })
            setIsConfigDialogVisible(false)
          }}
          onHide={() => setIsConfigDialogVisible(false)}
        />
      )}

      <Steps
        enabled={introEnabled}
        steps={introSteps}
        initialStep={0}
        onExit={() => setIntroEnabled(false)}
        options={introOptions}
      />

      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={true}
          debug={showDebugInfo}
        />
      )}

      {dataSettings.inputLinks.length > 0 && (
        <div
          className={`${styles.linkedKnowledge} mt-4 p-2 border rounded-md bg-gray-50`}
        >
          <h3 className="font-medium mb-2">Linked Knowledge:</h3>
          <div className="text-sm whitespace-pre-wrap">
            {data.linkedKnowledge}
          </div>
        </div>
      )}
    </NodeBaseContainer>
  )
}

export default memo(NodeQuickStartDynamic)
